type DurationUnit = {
	value: number;
	label?: string;
	useForZero?: boolean;
	zeroPadLength?: number;
};

type FormatDurationUnits = DurationUnit[];

export const formatShortDate = (date: Date | string) => {
	if (typeof date === "string") {
		date = new Date(date);
	}
	return Intl.DateTimeFormat(navigator.language, {
		year: "numeric",
		month: "numeric",
		day: "numeric",
	}).format(date);
};

export const formatShortTime = (date: Date | string) => {
	if (typeof date === "string") {
		date = new Date(date);
	}
	return Intl.DateTimeFormat(navigator.language, {
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
};

export const formatShortDateAndTime = (date: Date | string) => {
	if (typeof date === "string") {
		date = new Date(date);
	}
	return new Intl.DateTimeFormat(navigator.language, {
		year: "numeric",
		month: "numeric",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
};

/**
 * Formats a duration in seconds to a string with days, hours, minutes, and seconds
 * @param {number} seconds - The duration in seconds
 * @param {array} units - units and formatting info
 * @param {string} separator to insert between time components
 * @return {string} Formatted duration string
 */

export const formatDuration = (
	seconds: number,
	units: FormatDurationUnits,
	separator?: string,
) => {
	if (typeof seconds !== "number" || !Number.isFinite(seconds)) {
		return seconds;
	}

	let remainingSeconds = Math.abs(Math.round(seconds));
	const parts = [];

	if (seconds === 0) {
		for (const unit of units) {
			if (unit.useForZero) {
				const formattedZero = unit.zeroPadLength
					? "0".repeat(unit.zeroPadLength)
					: "0";
				parts.push(`${formattedZero}${unit.label ?? ""}`);
			}
		}
	} else {
		for (const unit of units) {
			const count = Math.floor(remainingSeconds / unit.value);

			if (count > 0 || unit.useForZero) {
				const formattedCount = unit.zeroPadLength
					? `${"0".repeat(unit.zeroPadLength)}${count}`.slice(
							-unit.zeroPadLength,
						)
					: `${count}`;
				parts.push(`${formattedCount}${unit.label ?? ""}`);
				remainingSeconds %= unit.value;
			}
		}
	}

	return parts.join(separator);
};

export const formatDurationClock = (seconds) =>
	formatDuration(
		seconds,
		[
			{ value: 3600 },
			{ value: 60, useForZero: true, zeroPadLength: 2 },
			{ value: 1, useForZero: true, zeroPadLength: 2 },
		],
		":",
	);

export const formatDurationHMS = (seconds) =>
	formatDuration(seconds, [
		{ value: 3600, label: "h" },
		{ value: 60, label: "m" },
		{ value: 1, label: "s", useForZero: true },
	]);

export const formatDurationClockShort = (seconds: number) =>
	formatDuration(
		seconds,
		[
			{ value: 60, useForZero: true },
			{ value: 1, useForZero: true, zeroPadLength: 2 },
		],
		":",
	);

export const formatNum = (value: number) =>
	new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);

export const formatWithMagnitude = (
	number: number | null | undefined,
): string => {
	if (number === undefined || number === null || number === 0) {
		return "0";
	}

	const formats = [
		{ minMagnitude: 9, divisor: 1000000000, suffix: "b" },
		{ minMagnitude: 6, divisor: 1000000, suffix: "m" },
		{ minMagnitude: 3, divisor: 1000, suffix: "k" },
		{ minMagnitude: 0, divisor: 1, suffix: "" },
	];

	const magnitude = Math.floor(Math.log10(number));
	const format = formats.find((f) => magnitude >= f.minMagnitude);
	const scaler = (10 * 10 ** (format.minMagnitude + 3 - magnitude)) / 100;
	return `${Math.floor(number / (format.divisor / scaler)) / scaler}${format.suffix}`;
};
/**
 * Convert bytes into human-readable size string (KB, MB, GB, etc.)
 * @param {number} bytes - the size in bytes
 * @param {number} decimals - number of decimals to show
 * @returns {string}
 */
export const formatBytesWithMagnitude = (
	bytes: number,
	decimals: number = 2,
) => {
	if (bytes === 0) return "0 Bytes";

	const k = 1024;
	const sizes = [" Bytes", "KB", "MB", "GB", "TB", "PB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Regions billed in euro: the twenty euro-area members, the four microstates
 * with an EU monetary agreement (AD, MC, SM, VA), and Montenegro and Kosovo,
 * which use the euro without one. Listed by region rather than derived from
 * the locale's own currency, because Intl exposes no currency-for-region
 * lookup and a locale like de-CH would otherwise resolve to CHF, which the
 * catalogue does not price in.
 */
const EURO_REGIONS = new Set([
	"AD",
	"AT",
	"BE",
	"CY",
	"DE",
	"EE",
	"ES",
	"FI",
	"FR",
	"GR",
	"HR",
	"IE",
	"IT",
	"LT",
	"LU",
	"LV",
	"MC",
	"ME",
	"MT",
	"NL",
	"PT",
	"SI",
	"SK",
	"SM",
	"VA",
	"XK",
]);

/**
 * ISO 4217 currency code inferred from the browser locale — GBP for GB, EUR
 * for the euro regions, otherwise USD.
 *
 * Must stay in step with the `currencies` map in config/plans.php: a code
 * returned here that the catalogue does not price in silently falls back to
 * its default_currency, so the user sees dollars with no explanation.
 */
export const getLocaleCurrencyCode = (
	locale: string = navigator.language,
): string => {
	let region: string | undefined;

	try {
		region = new Intl.Locale(locale).maximize().region ?? undefined;
	} catch {
		const subtag = locale.split("-")[1];
		if (subtag && /^[A-Za-z]{2}$/.test(subtag)) {
			region = subtag.toUpperCase();
		}
	}

	if (region === "GB") return "GBP";
	if (region && EURO_REGIONS.has(region)) return "EUR";
	return "USD";
};

/** Narrow symbol for a currency code (£, €, $). Falls back to the code. */
export const getCurrencySymbol = (currency: string): string => {
	try {
		return (
			new Intl.NumberFormat(undefined, {
				style: "currency",
				currency,
				currencyDisplay: "narrowSymbol",
			})
				.formatToParts(0)
				.find((part) => part.type === "currency")?.value ?? currency
		);
	} catch {
		return currency;
	}
};
